/**
 * V2Primitives — piezas visuales compartidas por las pantallas de V2.
 *
 * Nacieron en Hoy y las usa también Reservas: existen para que todas las
 * superficies V2 respiren igual y para no repartir decisiones visuales por
 * veinte ficheros. Nada de aquí llama a la API.
 *
 * Nota sobre la superficie: `dv-card` fija `padding: 1.5rem` en `index.css`
 * DESPUÉS de la capa de utilidades de Tailwind, así que una clase `p-0` o
 * `p-3` encima no gana. Para poder tener tarjetas compactas y listas a sangre
 * se pinta aquí la misma superficie (mismos colores, borde y radio que
 * `dv-card`) con el relleno bajo control.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Check } from "@phosphor-icons/react";
import Chip from "@/components/ui-dv/Chip";

/** Mismos colores y radio que `dv-card`, pero sin su relleno fijo. */
export const SURFACE =
  "bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-lg";

/* -------------------------------------------------------------------------
 * Bloques
 * ---------------------------------------------------------------------- */

/**
 * Bloque de la pantalla. El `id` sirve de ancla para los contadores del
 * resumen, que son navegación y no métricas de escaparate.
 *
 * Sin subtítulos explicativos: lo que hay que contar sobre el prototipo vive
 * en la nota de demostración de la cabecera, no encima de cada lista.
 */
export function TodayBlock({ id, title, count, action, children, testId }) {
  return (
    <section id={id} className="mb-6 scroll-mt-24" data-testid={testId}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <h2 className="font-serif-dv text-lg text-stone-900 dark:text-stone-100 min-w-0 truncate">
          {title}
          {typeof count === "number" && (
            <span className="ml-2 text-sm text-stone-400 tabular-nums align-middle">{count}</span>
          )}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Tarjeta. `padding` se pasa como clase para poder apretarla de verdad. */
export function TodayCard({ children, className = "", padding = "p-4", testId, tone }) {
  const accent = tone === "critical"
    ? "border-l-2 border-l-red-400 dark:border-l-red-700"
    : tone === "warn"
      ? "border-l-2 border-l-amber-400 dark:border-l-amber-700"
      : "";
  return (
    <div className={`${SURFACE} ${padding} ${accent} ${className}`} data-testid={testId}>
      {children}
    </div>
  );
}

/** Lista a sangre dentro de una tarjeta. */
export function RowList({ children, testId }) {
  return (
    <ul className="divide-y divide-stone-100 dark:divide-stone-800" data-testid={testId}>
      {children}
    </ul>
  );
}

export function Row({ children, testId, className = "" }) {
  return (
    <li className={`px-3.5 py-2.5 ${className}`} data-testid={testId}>
      {children}
    </li>
  );
}

/** Línea de contexto: unidad · hora · huésped, separada por puntos. */
export function Meta({ items, className = "" }) {
  const parts = items.filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <div className={`text-[11px] text-stone-500 dark:text-stone-400 truncate ${className}`}>
      {parts.join(" · ")}
    </div>
  );
}

export function StatusPill({ tone = "stone", children, dot = true, title, testId }) {
  return (
    <Chip tone={tone} size="xs" dot={dot} title={title} data-testid={testId}>{children}</Chip>
  );
}

export function EmptyState({ children, testId }) {
  return (
    <div
      className={`${SURFACE} p-4 text-sm text-stone-500 dark:text-stone-400 text-center`}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

/**
 * "Ver todo" — la palanca de la divulgación progresiva. Hoy enseña las
 * excepciones; el resto está a un toque, y el detalle completo vive en su
 * módulo.
 */
export function ShowMore({ expanded, onToggle, moreLabel, lessLabel = "Ver menos", testId }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      data-testid={testId}
      className="mt-2 text-xs text-stone-500 hover:text-stone-900 dark:hover:text-stone-100 min-h-[44px] inline-flex items-center"
    >
      {expanded ? lessLabel : moreLabel}
    </button>
  );
}

/* -------------------------------------------------------------------------
 * Acciones
 * ---------------------------------------------------------------------- */

/**
 * Acción de una fila. Una sola primaria por elemento: las secundarias no
 * navegan, exactamente el contrato de CTA que ya respeta el Action Center.
 */
export function QuickAction({
  label, onClick, to, primary = false, disabled, testId, icon: Icon,
  iconOnly = false, size = "touch",
}) {
  // `size="sm"` es solo para la tabla densa de escritorio, donde el ratón no
  // necesita 44 px y ese ancho se lo come todo. En móvil siempre "touch".
  const box = size === "sm"
    ? `h-7 text-[11px] ${iconOnly ? "w-7" : "px-2"}`
    : `min-h-[44px] text-xs ${iconOnly ? "w-11" : "px-3"}`;

  const cls = `inline-flex items-center justify-center gap-1.5 rounded-full transition-colors ${box} ${
    primary
      ? "bg-stone-900 text-white hover:bg-stone-700 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-stone-300"
      : "text-stone-600 dark:text-stone-300 border border-stone-300 dark:border-stone-700 hover:border-stone-500"
  } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`;

  const content = (
    <>
      {Icon && <Icon size={size === "sm" ? 12 : 13} weight="bold" />}
      {!iconOnly && label}
    </>
  );

  if (to && !disabled) {
    return (
      <Link to={to} className={cls} data-testid={testId} aria-label={iconOnly ? label : undefined}>
        {content}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cls}
      data-testid={testId}
      aria-label={iconOnly ? label : undefined}
      title={iconOnly ? label : undefined}
    >
      {content}
    </button>
  );
}

/**
 * Aviso discreto de que algo ha cambiado SOLO en la vista previa. Va fijo
 * abajo, respeta el área segura de iOS y no roba el foco.
 */
export function PreviewToast({ text, testId = "preview-toast" }) {
  if (!text) return null;
  return (
    <div
      role="status"
      data-testid={testId}
      className="fixed z-40 bottom-[calc(1rem_+_env(safe-area-inset-bottom,0px))] left-1/2 -translate-x-1/2 lg:left-auto lg:right-6 lg:translate-x-0 inline-flex items-center gap-2 text-xs text-stone-100 dark:text-stone-900 bg-stone-900/95 dark:bg-stone-100/95 rounded-full pl-2.5 pr-3.5 py-1.5 shadow-lg pointer-events-none max-w-[90vw]"
    >
      <Check size={12} weight="bold" className="shrink-0" />
      <span className="truncate">{text}</span>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Tabla densa — convención visual V2
 * ---------------------------------------------------------------------- */

/**
 * Convención para las tablas densas de V2, fijada aquí para que las futuras
 * pantallas (calendario, libro operativo, informes) no vuelvan a decidirlo:
 *
 *   - fondo alterno por COLUMNA, muy tenue, que guía la lectura vertical;
 *   - resalte de FILA solo al pasar el ratón;
 *   - nada de cebra fuerte por filas, que satura y compite con el contenido.
 *
 * Se usa solo en escritorio: en móvil las mismas filas se pintan como
 * tarjetas.
 */
const COLUMN_TINT = "bg-stone-50/70 dark:bg-stone-900/40";

export function DenseTable({ columns, rows, renderRow, testId, caption }) {
  return (
    <div className={`${SURFACE} overflow-x-auto`} data-testid={testId}>
      <table className="w-full text-xs border-collapse table-auto">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="text-left">
            {columns.map((c, i) => (
              <th
                key={c.key}
                scope="col"
                className={`px-2 py-1.5 text-[10px] uppercase tracking-widest font-normal text-stone-400 dark:text-stone-500 ${
                  i % 2 === 1 ? COLUMN_TINT : ""
                } ${c.align === "right" ? "text-right" : ""}`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={row.key}
              className="border-t border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-900/60 transition-colors"
              data-testid={row.testId}
            >
              {renderRow(row, rowIndex).map((cell, i) => (
                <td
                  key={columns[i].key}
                  className={`px-2 py-1.5 align-middle ${i % 2 === 1 ? COLUMN_TINT : ""} ${
                    columns[i].align === "right" ? "text-right" : ""
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
